/**
 * APP VERSION: 2.0.0 | DATE: 2026-03-19 | FILE: DispatchCupboards.jsx
 * ==========================================================================
 * DISPATCH CUPBOARDS UI — CSV-based inventory management
 *
 * CRITICAL BEHAVIORS (DO NOT CHANGE):
 * 1. Upload CSV → POST /api/dispatch-cupboards/upload-csv (fuzzy matches existing items)
 * 2. Export CSV → GET /api/dispatch-cupboards/export
 * 3. Template → GET /api/dispatch-cupboards/template
 * 4. Multi-select checkboxes with "Delete Selected" → POST /api/dispatch-cupboards/bulk-delete
 * 5. Fuzzy Match Tester → POST /api/dispatch-cupboards/test-match
 * 6. Stock editing → POST /api/dispatch-cupboards/bulk-set-stock
 * 7. Uses product_name (not sku) as the item identifier throughout
 * ==========================================================================
 */

import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  RefreshCw, Search, AlertTriangle, Package, ArrowDown, ArrowUp,
  History, Save, Download, Upload, FileDown, Trash2, FlaskConical, CheckSquare, Square
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StockBadge = ({ count }) => {
  if (count <= 0) return <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-red-100 text-red-700" data-testid="stock-badge-zero">Out of Stock</span>;
  if (count < 4) return <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-amber-100 text-amber-700" data-testid="stock-badge-low">Low: {count}</span>;
  return <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-emerald-100 text-emerald-700" data-testid="stock-badge-ok">{count}</span>;
};

const LogModal = ({ productName, onClose }) => {
  const [log, setLog] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/dispatch-cupboards/stock-log/${encodeURIComponent(productName)}`).then(res => {
      setLog((res.data.log || []).reverse());
    }).catch(() => toast.error("Failed to load log")).finally(() => setLoading(false));
  }, [productName]);

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg max-w-lg w-full max-h-[70vh] overflow-hidden" onClick={e => e.stopPropagation()} data-testid="stock-log-modal">
        <div className="p-4 border-b">
          <h3 className="font-semibold text-slate-900">Stock Log</h3>
          <p className="text-xs text-slate-500">{productName}</p>
        </div>
        <div className="p-4 overflow-y-auto max-h-[50vh]">
          {loading ? <RefreshCw className="w-5 h-5 animate-spin mx-auto" /> : log.length === 0 ? (
            <p className="text-sm text-slate-400 text-center">No stock changes recorded</p>
          ) : (
            <div className="space-y-2">
              {log.map((entry, i) => (
                <div key={i} className="flex items-center gap-3 text-sm p-2 rounded bg-slate-50">
                  {entry.change > 0 ? <ArrowUp className="w-4 h-4 text-emerald-500 shrink-0" /> : <ArrowDown className="w-4 h-4 text-red-500 shrink-0" />}
                  <div className="flex-1">
                    <span className={`font-mono font-semibold ${entry.change > 0 ? "text-emerald-600" : "text-red-600"}`}>
                      {entry.change > 0 ? "+" : ""}{entry.change}
                    </span>
                    <span className="text-slate-500 mx-2">({entry.old_count} → {entry.new_count})</span>
                    <span className="text-slate-600">{entry.reason}</span>
                  </div>
                  <span className="text-xs text-slate-400 shrink-0">{new Date(entry.timestamp).toLocaleDateString()}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="p-3 border-t">
          <Button variant="outline" size="sm" onClick={onClose} className="w-full" data-testid="close-log-modal">Close</Button>
        </div>
      </div>
    </div>
  );
};

export default function DispatchCupboards() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [search, setSearch] = useState("");
  const [editStocks, setEditStocks] = useState({});
  const [saving, setSaving] = useState(false);
  const [logItem, setLogItem] = useState(null);
  const [lowStockItems, setLowStockItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [matchTest, setMatchTest] = useState("");
  const [matchResult, setMatchResult] = useState(null);
  const [testing, setTesting] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [deleting, setDeleting] = useState(false);
  const fileInputRef = useRef(null);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const [itemsRes, lowRes] = await Promise.all([
        axios.get(`${API}/dispatch-cupboards/items`),
        axios.get(`${API}/dispatch-cupboards/low-stock`),
      ]);
      setItems(itemsRes.data.items || []);
      setLowStockItems(lowRes.data.items || []);
    } catch {
      toast.error("Failed to load dispatch cupboard data");
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const handleCSVUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await axios.post(`${API}/dispatch-cupboards/upload-csv`, formData);
      const { added, updated, fuzzy_matches } = res.data;
      let msg = `CSV uploaded: ${added} added, ${updated} updated`;
      if (fuzzy_matches?.length) {
        msg += ` (${fuzzy_matches.length} fuzzy-matched)`;
      }
      toast.success(msg);
      if (fuzzy_matches?.length) {
        fuzzy_matches.forEach(m => toast.info(`"${m.csv_name}" → "${m.matched_to}"`, { duration: 5000 }));
      }
      fetchItems();
    } catch (err) {
      toast.error(err.response?.data?.detail || "CSV upload failed");
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleStockEdit = (name, value) => {
    setEditStocks(prev => ({ ...prev, [name]: value }));
  };

  const saveStocks = async () => {
    const toSave = Object.entries(editStocks)
      .filter(([, v]) => v !== "" && v !== undefined)
      .map(([product_name, count]) => ({ product_name, count: parseInt(count) || 0 }));
    if (toSave.length === 0) return;
    setSaving(true);
    try {
      await axios.post(`${API}/dispatch-cupboards/bulk-set-stock`, { items: toSave });
      toast.success(`Updated stock for ${toSave.length} items`);
      setEditStocks({});
      fetchItems();
    } catch {
      toast.error("Stock update failed");
    }
    setSaving(false);
  };

  const deleteItem = async (name) => {
    if (!window.confirm(`Delete "${name}" from Dispatch Cupboards?`)) return;
    try {
      await axios.delete(`${API}/dispatch-cupboards/item/${encodeURIComponent(name)}`);
      toast.success("Item deleted");
      setSelected(prev => { const n = new Set(prev); n.delete(name); return n; });
      fetchItems();
    } catch {
      toast.error("Delete failed");
    }
  };

  const bulkDelete = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Delete ${selected.size} selected items? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      const res = await axios.post(`${API}/dispatch-cupboards/bulk-delete`, { product_names: [...selected] });
      toast.success(`Deleted ${res.data.deleted} of ${res.data.total} items`);
      setSelected(new Set());
      fetchItems();
    } catch {
      toast.error("Bulk delete failed");
    }
    setDeleting(false);
  };

  const runMatchTest = async () => {
    if (!matchTest.trim()) return;
    setTesting(true);
    try {
      const res = await axios.post(`${API}/dispatch-cupboards/test-match`, { product_name: matchTest });
      setMatchResult(res.data);
    } catch {
      toast.error("Match test failed");
    }
    setTesting(false);
  };

  const filteredItems = items.filter(item => {
    const matchSearch = !search ||
      item.product_name?.toLowerCase().includes(search.toLowerCase());
    if (!matchSearch) return false;
    if (filter === "low") return item.stock_count > 0 && item.stock_count < 4;
    if (filter === "out") return item.stock_count <= 0;
    return true;
  });

  const hasEdits = Object.keys(editStocks).length > 0;

  const toggleSelect = (name) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(name) ? next.delete(name) : next.add(name);
      return next;
    });
  };

  const allFilteredSelected = filteredItems.length > 0 && filteredItems.every(i => selected.has(i.product_name));

  const toggleSelectAll = () => {
    if (allFilteredSelected) {
      setSelected(prev => {
        const next = new Set(prev);
        filteredItems.forEach(i => next.delete(i.product_name));
        return next;
      });
    } else {
      setSelected(prev => {
        const next = new Set(prev);
        filteredItems.forEach(i => next.add(i.product_name));
        return next;
      });
    }
  };

  return (
    <div className="space-y-6" data-testid="dispatch-cupboards-page">
      {/* Low Stock Alerts */}
      {lowStockItems.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4" data-testid="low-stock-alert">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-5 h-5 text-amber-600" />
            <h3 className="font-semibold text-amber-800">Low Stock Alert ({lowStockItems.length} items below 4 units)</h3>
          </div>
          <div className="flex flex-wrap gap-2">
            {lowStockItems.slice(0, 10).map(item => (
              <span key={item.product_name} className="inline-flex items-center gap-1 px-2 py-1 rounded bg-amber-100 text-xs text-amber-800">
                <Package className="w-3 h-3" /> {item.product_name}: <b>{item.stock_count}</b>
              </span>
            ))}
            {lowStockItems.length > 10 && <span className="text-xs text-amber-600">+{lowStockItems.length - 10} more</span>}
          </div>
        </div>
      )}

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-3">
        <input type="file" ref={fileInputRef} accept=".csv" onChange={handleCSVUpload} className="hidden" data-testid="csv-file-input" />
        <Button onClick={() => fileInputRef.current?.click()} disabled={uploading} variant="default" size="sm" data-testid="upload-csv-btn">
          <Upload className={`w-4 h-4 mr-1 ${uploading ? "animate-spin" : ""}`} />
          {uploading ? "Uploading..." : "Upload CSV"}
        </Button>
        <Button onClick={() => window.open(`${API}/dispatch-cupboards/template`, "_blank")} variant="outline" size="sm" data-testid="download-template-btn">
          <FileDown className="w-4 h-4 mr-1" /> Template
        </Button>
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-slate-400" />
          <Input placeholder="Search product name..." value={search} onChange={e => setSearch(e.target.value)}
            className="pl-9 h-9" data-testid="search-input" />
        </div>
        <div className="flex gap-1">
          {["all", "low", "out"].map(f => (
            <Button key={f} size="sm" variant={filter === f ? "default" : "outline"}
              onClick={() => setFilter(f)} data-testid={`filter-${f}`}
              className="text-xs capitalize">{f === "out" ? "Out of Stock" : f === "low" ? "Low Stock" : "All"}</Button>
          ))}
        </div>
        <Button onClick={() => window.open(`${API}/dispatch-cupboards/export`, "_blank")} variant="outline" size="sm" data-testid="export-csv-btn">
          <Download className="w-4 h-4 mr-1" /> Export CSV
        </Button>
        {hasEdits && (
          <Button onClick={saveStocks} disabled={saving} size="sm" data-testid="save-stocks-btn">
            <Save className="w-4 h-4 mr-1" /> {saving ? "Saving..." : `Save (${Object.keys(editStocks).length})`}
          </Button>
        )}
        {selected.size > 0 && (
          <Button onClick={bulkDelete} disabled={deleting} size="sm" variant="destructive" data-testid="bulk-delete-btn">
            <Trash2 className="w-4 h-4 mr-1" /> {deleting ? "Deleting..." : `Delete Selected (${selected.size})`}
          </Button>
        )}
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: "Total Items", value: items.length, color: "bg-slate-100 text-slate-800" },
          { label: "In Stock", value: items.filter(i => i.stock_count > 0).length, color: "bg-emerald-50 text-emerald-800" },
          { label: "Low Stock (<4)", value: lowStockItems.length, color: "bg-amber-50 text-amber-800" },
          { label: "Out of Stock", value: items.filter(i => i.stock_count <= 0).length, color: "bg-red-50 text-red-800" },
        ].map(s => (
          <div key={s.label} className={`rounded-lg p-3 ${s.color}`}>
            <p className="text-xs font-medium opacity-70">{s.label}</p>
            <p className="text-2xl font-bold">{s.value}</p>
          </div>
        ))}
      </div>

      {/* Fuzzy Match Tester */}
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-4" data-testid="fuzzy-match-tester">
        <div className="flex items-center gap-2 mb-2">
          <FlaskConical className="w-4 h-4 text-slate-600" />
          <h4 className="text-sm font-semibold text-slate-700">Fuzzy Match Tester</h4>
        </div>
        <div className="flex gap-2">
          <Input placeholder='e.g. "Tripod Stand - Black for iPhone"' value={matchTest}
            onChange={e => setMatchTest(e.target.value)} className="flex-1 h-9 text-sm"
            onKeyDown={e => e.key === "Enter" && runMatchTest()} data-testid="match-test-input" />
          <Button onClick={runMatchTest} disabled={testing} size="sm" variant="outline" data-testid="match-test-btn">
            {testing ? <RefreshCw className="w-4 h-4 animate-spin" /> : "Test"}
          </Button>
        </div>
        {matchResult && (
          <div className="mt-2 text-sm" data-testid="match-test-result">
            <span className="text-slate-500">Input:</span> <span className="text-slate-800">{matchResult.order_product}</span>
            <span className="mx-2 text-slate-300">&rarr;</span>
            {matchResult.matched_to ? (
              <span className="text-emerald-700 font-medium">{matchResult.matched_to}</span>
            ) : (
              <span className="text-red-600 font-medium">No match found</span>
            )}
          </div>
        )}
      </div>

      {/* Items Table */}
      {loading ? (
        <div className="flex justify-center p-12">
          <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-12 text-slate-400">
          <Package className="w-10 h-10 mx-auto mb-3 opacity-50" />
          <p className="font-medium">No items in Dispatch Cupboards</p>
          <p className="text-sm mt-1">Upload a CSV to populate your inventory. Download the template to see the expected format.</p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="dispatch-items-table">
              <thead className="bg-slate-50 border-b text-slate-500">
                <tr>
                  <th className="w-10 p-3 text-center">
                    <button onClick={toggleSelectAll} className="text-slate-400 hover:text-slate-700 transition-colors" data-testid="select-all-checkbox">
                      {allFilteredSelected ? <CheckSquare className="w-4 h-4 text-blue-600" /> : <Square className="w-4 h-4" />}
                    </button>
                  </th>
                  <th className="text-left p-3 font-medium">Product Name</th>
                  <th className="text-center p-3 font-medium">Current Stock</th>
                  <th className="text-center p-3 font-medium w-32">Set Stock</th>
                  <th className="text-center p-3 font-medium w-28">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filteredItems.map(item => {
                  const isSelected = selected.has(item.product_name);
                  return (
                    <tr key={item.product_name}
                      className={`transition-colors ${isSelected ? "bg-blue-50/60" : item.stock_count < 4 && item.stock_count > 0 ? "bg-amber-50/30" : item.stock_count <= 0 ? "bg-red-50/30" : "hover:bg-slate-50/50"}`}
                      data-testid={`item-row-${item.product_name}`}>
                      <td className="w-10 p-3 text-center">
                        <button onClick={() => toggleSelect(item.product_name)}
                          className="text-slate-400 hover:text-blue-600 transition-colors"
                          data-testid={`select-${item.product_name}`}>
                          {isSelected ? <CheckSquare className="w-4 h-4 text-blue-600" /> : <Square className="w-4 h-4" />}
                        </button>
                      </td>
                      <td className="p-3">
                        <p className="font-medium text-slate-800 text-sm leading-tight">{item.product_name}</p>
                        {item.last_updated && <p className="text-xs text-slate-400">Updated: {new Date(item.last_updated).toLocaleDateString()}</p>}
                      </td>
                      <td className="p-3 text-center">
                        <StockBadge count={item.stock_count} />
                      </td>
                      <td className="p-3 text-center">
                        <Input type="number" min="0" placeholder={String(item.stock_count)}
                          value={editStocks[item.product_name] ?? ""}
                          onChange={e => handleStockEdit(item.product_name, e.target.value)}
                          className="h-8 w-20 text-center mx-auto text-sm"
                          data-testid={`stock-input-${item.product_name}`} />
                      </td>
                      <td className="p-3 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <button onClick={() => setLogItem(item.product_name)}
                            className="text-slate-400 hover:text-blue-600 transition-colors p-1"
                            data-testid={`log-btn-${item.product_name}`} title="Stock Log">
                            <History className="w-4 h-4" />
                          </button>
                          <button onClick={() => deleteItem(item.product_name)}
                            className="text-slate-400 hover:text-red-600 transition-colors p-1"
                            data-testid={`delete-btn-${item.product_name}`} title="Delete">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {filteredItems.length === 0 && (
            <div className="text-center py-8 text-slate-400 text-sm">No items match your filter</div>
          )}
        </div>
      )}

      {logItem && <LogModal productName={logItem} onClose={() => setLogItem(null)} />}
    </div>
  );
}
